/**
 * CMMC on Today and /programs — V102-8-1 (+ G7-H3, PR A panel F6/F7). Both
 * render the SAME model as the scorecard (`useCmmcCards` → `buildScorecard`):
 *
 *   CmmcTodayItems   a row per clock under 30 days (red < 14, amber 14–30)
 *                    with its fix, plus "Post score · ready". A red row's action
 *                    is the page's single filled primary (Today drops "Next" to
 *                    secondary).
 *   CmmcProgramCard  the program's card: score / 110 (or the Level 1 result),
 *                    the verdict with its mark, the clock chip in the clock's
 *                    tone, and the scorecard's primary (hidden when a Today row
 *                    already carries the same action). Never "On track" while
 *                    the verdict is red or a red clock runs.
 *
 * Only rendered for cards GET /programs returned (the server lists a planned
 * program only under the staging preview). Reads: any member. No writes.
 */
import { Link } from 'react-router-dom';
import { Alert, Spinner, StageChip } from '@aigovops/design-system';
import { periodStages, programTitle } from '../../lib/nav-config';
import { actionHref, cmmcTodayItems, type TodayItem } from '../../lib/cmmc-model';
import type { CmmcCardState } from '../../lib/use-cmmc';
import { Chip } from './shared';
import '../../pages/cmmc.css';

export function todayItemsOf(states: readonly CmmcCardState[]): TodayItem[] {
  return states.flatMap((s) => (s.model
    ? cmmcTodayItems(s.model, s.prepareHref, programTitle(s.card.program, s.card.report_type)).map((i) => ({ ...i, key: `${s.card.profile_id}:${i.key}` }))
    : []));
}

/** Today's CMMC rows (above everything else). The first red row's action is the page's one filled primary. */
export function CmmcTodayItems({ items }: { items: readonly TodayItem[] }) {
  if (!items.length) return null;
  const primaryKey = items.find((i) => i.tone === 'red')?.key;
  return (
    <ul className="gp-cmmc-deadlines gp-plain-list" aria-label="Program deadlines">
      {items.map((c) => (
        <li key={c.key} className="gp-cmmc-deadline" data-part="cmmc-deadline" data-tone={c.tone}>
          <div>
            <h2 id={`cmmc-dl-${c.key}`}>{c.title}</h2>
            <p>{c.detail}</p>
          </div>
          <span className="gp-actions">
            <Link className={`gp-linkbtn${c.key === primaryKey ? ' btn-primary' : ''}`} to={c.action.href} aria-label={`${c.action.label} · ${c.title}`}>{c.action.label}</Link>
            {c.secondary ? <Link className="gp-cmmc-quiet" to={c.secondary.href} data-part="deadline-secondary">{c.secondary.label}</Link> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** The customer deadline as plain toned text (no box); Provisional keeps the only chip. */
function CustomerText({ m }: { m: NonNullable<CmmcCardState['model']> }) {
  return m.customer ? <span className="gp-cmmc-toned" data-part="customer-deadline" data-tone={m.customer.tone}>{m.customer.text}</span> : null;
}

/** Today: the CMMC card inside the Programs list (an li, like its siblings). */
export function CmmcProgramCard({ state, hideHrefs }: { state: CmmcCardState; hideHrefs?: ReadonlySet<string> }) {
  const { card, period, model: m, prepareHref } = state;
  const base = prepareHref.replace(/\/prepare$/, '');
  const title = programTitle(card.program, card.report_type);
  const href = m?.primary ? actionHref(m.primary, prepareHref) : null;
  return (
    <li className="gp-pcard gp-cmmc-today" data-program={card.program} data-part="cmmc-today">
      <h3><Link to={base}>{title}</Link></h3>
      {!period ? (
        <p className="gp-pcard-status"><span>No assessment window yet</span><Link className="gp-linkbtn gp-linkbtn--compact" to={`${base}/prepare`}>Set the window</Link></p>
      ) : !m ? (
        state.error
          ? <Alert variant="error"><p>The score could not load.</p><Link to={`${base}/prepare`}>Open the scorecard</Link></Alert>
          : <Spinner label="Loading the score" />
      ) : <>
        {m.l2 ? (
          <p className="gp-cmmc-figure" data-part="score"><b>{m.l2.score}</b><span className="gp-dim">{` pts / ${m.l2.max}`}</span></p>
        ) : (
          <p className="gp-cmmc-figure" data-part="score"><b>{`${m.l1.metCount} of ${m.l1.total}`}</b><span className="gp-dim"> Level 1 requirements met</span></p>
        )}
        <p className="gp-cmmc-verdict" data-part="verdict" data-tone={m.verdict.tone}>
          <span className="gp-cmmc-mark" data-mark={m.verdict.mark} aria-hidden="true" />
          <strong>{m.verdict.text}</strong>
          {m.provisional ? <Chip text={m.provisional} tone="outline" /> : null}
        </p>
        {m.clock && m.clock.kind === 'closeout' ? <p className="gp-cmmc-verdict" data-part="clock"><Chip text={`POA&M ${m.clock.text}`} tone={m.clock.tone} /></p> : null}
        {m.customer && m.customer.tone !== 'neutral' ? <p className="gp-pcard-status"><CustomerText m={m} /></p> : null}
        {m.primary && href && !hideHrefs?.has(href) ? (
          <p className="gp-pcard-next">
            <Link className="gp-linkbtn gp-linkbtn--compact" to={href} data-part="cmmc-today-action">{m.primary.label}</Link>
          </p>
        ) : null}
      </>}
    </li>
  );
}

/**
 * /programs: the CMMC row in its siblings' structure (gp-program-row: name +
 * period chips, the headline, then the "Next" slot with the right-aligned
 * action). The Next slot carries the customer deadline and the ▲ delta.
 */
export function CmmcProgramRow({ state, canManage = false }: { state: CmmcCardState; canManage?: boolean }) {
  const { card, period, model: m, prepareHref } = state;
  const base = prepareHref.replace(/\/prepare$/, '');
  const title = programTitle(card.program, card.report_type);
  const href = m?.primary ? actionHref(m.primary, prepareHref) : `${base}/prepare`;
  const label = m?.primary?.label ?? 'Open';
  return (
    <li className="gp-program-row" data-program={card.program} data-part="cmmc-today">
      <div className="gp-program-name">
        <Link to={base}>{title}</Link>
        <span className="gp-program-periods">
          {card.periods.filter((p) => p.active).map((p, i) => (
            <span key={`${p.label}:${i}`} className="gp-program-period">{p.label} {periodStages(p).map((st) => <StageChip key={st} stage={st} />)}</span>
          ))}
        </span>
      </div>
      {!period ? <p className="gp-program-headline">No assessment window yet</p>
        : !m ? (state.error ? <Alert variant="error"><p>The score could not load.</p></Alert> : <Spinner label="Loading the score" />)
        : <>
          <p className="gp-program-headline gp-cmmc-verdict" data-part="verdict" data-tone={m.verdict.tone}>
            <span className="gp-cmmc-mark" data-mark={m.verdict.mark} aria-hidden="true" />
            <strong>{m.verdict.text}</strong>
            <span className="gp-dim" data-part="score">{m.l2 ? `${m.l2.score} pts / ${m.l2.max}` : `${m.l1.metCount} of ${m.l1.total} met`}</span>
            {m.clock && m.clock.kind === 'closeout' ? <Chip text={`POA&M ${m.clock.text}`} tone={m.clock.tone} /> : null}
          </p>
        </>}
      <div className="gp-program-next">
        <span data-part="next-slot">
          {m ? <CustomerText m={m} /> : null}
          {m?.l2?.delta ? <span className="gp-cmmc-delta" data-part="delta">{m.customer ? ` · ${m.l2.delta}` : m.l2.delta}</span> : null}
          {m && !m.customer && !m.closeout ? <span className="gp-dim" data-part="no-deadline">{m.l2?.delta ? ' · ' : ''}No customer deadline{canManage ? <>{' · '}<Link className="gp-cmmc-hit" to={`${base}/prepare#cmmc-add-trigger`}>Add</Link></> : null}</span> : null}
        </span>
        <Link className="gp-linkbtn gp-linkbtn--compact" to={href} data-part="cmmc-today-action" aria-label={`${label} · ${title}`}>{label}</Link>
      </div>
    </li>
  );
}
